<template>
  <div class="space-y-4">
    <div
      v-for="(section, index) in SECTIONS"
      :key="index"
      class="bg-background-neutral-secondary rounded-[20px]"
    >
      <button
        class="flex w-full items-center justify-between p-4 text-left font-semibold"
        type="button"
        @click="toggleAccordion(index)"
      >
        <span class="text-text-neutral-primary text-base">
          {{ t(`cashbackInfo.faq.${section.key}.question`) }}
        </span>
        <ChevronDownIcon
          class="text-situational-black shrink-0 transition-transform duration-300"
          :class="{ 'rotate-180': expandedSections[index] }"
        />
      </button>

      <WithAccordionFadeTransition :is-expanded="expandedSections[index]">
        <div v-if="index === 0" class="space-y-4 px-4 pb-4">
          <p class="text-text-neutral-secondary text-sm">
            {{ t('cashbackInfo.faq.loyalty-levels.answer') }}
          </p>
          <div class="flex flex-col gap-4">
            <div
              class="text-text-neutral-tertiary flex gap-6 text-xs font-medium"
            >
              <span class="flex-1">
                {{ t('cashbackInfo.faq.loyalty-levels.level') }}
              </span>
              <span class="w-[72px] text-right">
                {{ t('cashbackInfo.faq.loyalty-levels.balance') }}
              </span>
              <span class="w-11 text-right">
                {{ t('cashbackInfo.cashback') }}
              </span>
            </div>
            <div
              v-for="level in cashbackLevels"
              :key="level.name"
              class="text-text-neutral-primary flex items-center gap-6"
              :class="{
                'bg-background-neutral-onblock -mx-2 rounded-xl px-2 py-1.5':
                  level.isActive,
              }"
            >
              <div class="flex flex-1 items-center gap-2">
                <img
                  :src="level.icon"
                  width="20"
                  height="20"
                  class="shrink-0"
                  alt=""
                />
                <span class="text-sm font-semibold">{{ level.name }}</span>
              </div>
              <span
                class="w-min text-right text-sm font-semibold whitespace-nowrap"
              >
                {{
                  t('cashbackInfo.faq.loyalty-levels.from', {
                    balance: `${prettyPrice(String(level.balance))} ₸`,
                  })
                }}
              </span>
              <span class="w-11 text-right text-sm font-semibold">
                {{ level.cashback }}%
              </span>
            </div>
          </div>
          <div class="relative flex gap-3 rounded-[20px] bg-[#1d283c] p-4">
            <div
              class="pointer-events-none absolute top-4 right-0 h-[80px] w-[70%]"
            >
              <div class="absolute inset-0 overflow-hidden">
                <img
                  :src="depositBannerImg"
                  alt=""
                  class="absolute top-1/2 right-0 -translate-y-1/2"
                  width="145"
                  height="80"
                />
              </div>
              <div
                class="absolute inset-0 bg-gradient-to-r from-[#1d283c] to-transparent to-80%"
              />
            </div>
            <div class="relative flex flex-col gap-1">
              <p class="text-base font-semibold text-white">
                {{ t('cashbackInfo.faq.loyalty-levels.maximum-cashback') }}
              </p>
              <p class="max-w-[80%] text-sm text-white opacity-90">
                {{ t('cashbackInfo.faq.loyalty-levels.deposit-suggestion') }}
              </p>
            </div>
          </div>
        </div>

        <div v-else-if="index === 1" class="space-y-3 px-4 pb-4">
          <p class="text-text-neutral-secondary text-sm">
            {{ t('cashbackInfo.faq.how-it-works.body') }}
          </p>
          <p class="text-text-neutral-secondary text-sm">
            {{ t('cashbackInfo.faq.how-it-works.footer') }}
          </p>
        </div>
      </WithAccordionFadeTransition>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { prettyPrice } from '@aviata/text-fns';

  import { WithAccordionFadeTransition } from '../../../shared/ui';
  import type { CashbackLevel } from '../types';
  import depositBannerImg from '../assets/deposit-visual.webp';
  import ChevronDownIcon from '../assets/24/chevron-down.svg?component';
  import messages from '../locales.json';

  defineProps<{
    cashbackLevels: CashbackLevel[];
  }>();

  const { t } = useI18n({ messages });

  const SECTIONS = [
    { key: 'loyalty-levels' },
    { key: 'how-it-works' },
  ] as const;

  const expandedSections = ref<boolean[]>(SECTIONS.map(() => false));

  const toggleAccordion = (index: number) => {
    expandedSections.value[index] = !expandedSections.value[index];
  };
</script>

<style scoped>
  .cashback-return-info {
    background: linear-gradient(262.23deg, #01d58a -1.12%, #006fc2 101.31%);
    background-clip: text;
    color: transparent;
  }
</style>
